import { useLayoutEffect, useRef, type RefObject } from 'react'

export function useScrollToFirstAccordionOnTabChange(
  stickyTabsRef: RefObject<HTMLElement | null>,
  sectionAnchorRef: RefObject<HTMLElement | null>,
  selectedTabIndex: number
) {
  const previousTabIndexRef = useRef(selectedTabIndex)

  useLayoutEffect(() => {
    if (previousTabIndexRef.current === selectedTabIndex) {
      return undefined
    }

    previousTabIndexRef.current = selectedTabIndex

    const stickyTabsElement = stickyTabsRef.current
    const sectionAnchorElement = sectionAnchorRef.current
    if (!stickyTabsElement || !sectionAnchorElement) {
      return undefined
    }

    const scrollToFirstAccordion = () => {
      scrollPlanRecommendationSheetToFirstAccordion(stickyTabsElement, sectionAnchorElement)
    }

    scrollToFirstAccordion()
    const frameId = window.requestAnimationFrame(scrollToFirstAccordion)

    return () => {
      window.cancelAnimationFrame(frameId)
    }
  }, [sectionAnchorRef, selectedTabIndex, stickyTabsRef])
}

export function findPlanRecommendationOverflowScrollParent(element: HTMLElement): HTMLElement | null {
  let parent = element.parentElement
  let scrollParent: HTMLElement | null = null

  while (parent) {
    const { overflowY } = window.getComputedStyle(parent)
    if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
      scrollParent = parent
    }
    parent = parent.parentElement
  }

  return scrollParent
}

export function scrollPlanRecommendationSheetToFirstAccordion(
  stickyTabsElement: HTMLElement,
  sectionAnchorElement: HTMLElement
) {
  const scrollParent = findPlanRecommendationOverflowScrollParent(sectionAnchorElement)
  if (!scrollParent) {
    sectionAnchorElement.scrollIntoView({ block: 'start', behavior: 'auto' })
    return
  }

  const stickyOffset = stickyTabsElement.getBoundingClientRect().height
  const anchorRect = sectionAnchorElement.getBoundingClientRect()
  const containerRect = scrollParent.getBoundingClientRect()
  const anchorTopInContainer = anchorRect.top - containerRect.top
  const maxScrollTop = scrollParent.scrollHeight - scrollParent.clientHeight
  const targetScrollTop = Math.max(
    0,
    Math.min(scrollParent.scrollTop + anchorTopInContainer - stickyOffset, maxScrollTop)
  )

  scrollWithoutOverflowAnchoring(scrollParent, () => {
    scrollParent.scrollTo({ top: targetScrollTop, behavior: 'auto' })
  })
}

export function scrollPlanRecommendationSheetToElement(element: HTMLElement) {
  const scrollParent = findPlanRecommendationOverflowScrollParent(element)
  if (!scrollParent) {
    element.scrollIntoView({ block: 'start', behavior: 'auto' })
    return
  }

  const elementRect = element.getBoundingClientRect()
  const containerRect = scrollParent.getBoundingClientRect()
  const elementTopInContainer = elementRect.top - containerRect.top
  const maxScrollTop = scrollParent.scrollHeight - scrollParent.clientHeight
  const targetScrollTop = Math.max(
    0,
    Math.min(scrollParent.scrollTop + elementTopInContainer, maxScrollTop)
  )

  scrollWithoutOverflowAnchoring(scrollParent, () => {
    scrollParent.scrollTo({ top: targetScrollTop, behavior: 'auto' })
  })
}

export function scrollPlanRecommendationSheetToBottom(anchorElement: HTMLElement) {
  const scrollParent = findPlanRecommendationOverflowScrollParent(anchorElement)
  if (!scrollParent) {
    return
  }

  scrollWithoutOverflowAnchoring(scrollParent, () => {
    scrollParent.scrollTop = scrollParent.scrollHeight - scrollParent.clientHeight
  })
}

function scrollWithoutOverflowAnchoring(
  scrollParent: HTMLElement,
  applyScroll: () => void
) {
  const previousOverflowAnchor = scrollParent.style.overflowAnchor
  scrollParent.style.overflowAnchor = 'none'
  applyScroll()
  window.requestAnimationFrame(() => {
    scrollParent.style.overflowAnchor = previousOverflowAnchor
  })
}
